import { assert } from "./project";

export type Identification = {
  canonicalUri: string;
  version: string | undefined;
  canonicalVersionUri: string;
};

export type KeyColumn = {
  id: string;
  ref: string | undefined;
};

export type ColumnSet = {
  defaultCodeKey: string;
  defaultNameKey: string | undefined;
  columns: string[];
};

export class CodeList {
  constructor(
    public identifier: Identification,
    public defaultCodeKey: string,
    public defaultNameKey: string | undefined,
    public columns: Record<string, (string | null)[]>,
  ) {}

  public hasColumn(column: string): boolean {
    return column in this.columns;
  }

  public getKeyColumn(column: string | undefined = undefined): string[] {
    /**
     * Return either the specified column or the default key column
     * as a unique list of values.
     * This fails if the requested column has duplicate or None values.
     * */

    if (column === undefined) {
      column = this.defaultCodeKey;
    }

    const items = this.columns[column];
    if (items === undefined) {
      throw new Error(`Unknown column '${column}'`);
    }

    const itemSet: Set<string> = new Set();
    for (const item of items) {
      if (item === null) {
        throw new Error(`Missing value in key column '${column}'`);
      } else if (itemSet.has(item)) {
        throw new Error(`Duplicate value '${item}' in key column`);
      } else {
        itemSet.add(item);
      }
    }

    // We checked already that `None` is not in the list
    return items as string[];
  }

  public selectNameColumn(): string[] {
    /**
     * Try to select a name column.
     * If no default column was specified in the code list, try some
     * conventions from FRED Classic.
     * */

    if (this.defaultNameKey !== undefined) {
      return this.getColumn(this.defaultNameKey);
    } else if (this.hasColumn("Codename")) {
      return this.getColumn("Codename");
    } else if (this.hasColumn("name")) {
      return this.getColumn("name");
    } else {
      throw new Error(
        `Cannot select name column for code list ${this.identifier.canonicalVersionUri}`,
      );
    }
  }

  private getColumn(column: string): string[] {
    /**
     * Return the specified column.
     * This fails if the requested column has None values.
     * */

    const items = this.columns[column];
    if (items === undefined) {
      throw new Error(`Unknown column '${column}'`);
    }

    for (const item of items) {
      if (item === null) {
        throw new Error(`Missing value in key column '${column}'`);
      }
    }

    // We checked already that `None` is not in the list
    return items as string[];
  }
}

export function parseCodeList(value: string): CodeList {
  const parser = new DOMParser();
  const doc: XMLDocument = parser.parseFromString(value, "text/xml");

  try {
    for (const child of doc.children) {
      switch (child.localName) {
        case "CodeList":
          return parseCodeListNode(child);
        default:
          continue;
      }
    }

    throw new Error("No CodeList element found");
  } catch (e: any) {
    throw new Error(`Could not parse code list: ${e.message}`);
  }
}

function parseCodeListNode(node: Element): CodeList {
  let identification: Identification | undefined;
  let columnSet: ColumnSet | undefined;
  let rows: Record<string, string>[] | undefined;

  for (const child of node.children) {
    switch (child.localName) {
      case "Identification":
        identification = parseIdentification(child);
        break;
      case "ColumnSet":
        columnSet = parseColumnSet(child);
        break;
      case "SimpleCodeList":
        rows = parseSimpleCodeList(child);
        break;
      default:
        continue;
    }
  }

  if (!identification) throw new Error("Missing <Identification>");
  if (!columnSet) throw new Error("Missing <ColumnSet>");
  if (!rows) throw new Error("Missing <SimpleCodeList>");

  return new CodeList(
    identification,
    columnSet.defaultCodeKey,
    columnSet.defaultNameKey,
    filterItems(columnSet, rows),
  );
}

function filterItems(
  columnSet: ColumnSet,
  rows: Record<string, string>[],
): Record<string, (string | null)[]> {
  const columns: Record<string, (string | null)[]> = {};
  for (const key of columnSet.columns) {
    columns[key] = [];
  }

  for (const row of rows) {
    for (const key of columnSet.columns) {
      columns[key].push(row[key]);
    }
  }

  return columns;
}

function parseIdentification(node: Element): Identification {
  let version: string | undefined;
  let canonicalUri: string | undefined;
  let canonicalVersionUri: string | undefined;

  for (const child of node.children) {
    switch (child.localName) {
      case "Version":
        version = parseOptionalContent(child);
        break;
      case "CanonicalUri":
        canonicalUri = parseContent(child);
        break;
      case "CanonicalVersionUri":
        canonicalVersionUri = parseContent(child);
        break;
      default:
        continue;
    }
  }

  if (!canonicalUri) throw new Error("Missing CanonicalUri");
  if (!canonicalVersionUri) throw new Error("Missing CanonicalVersionUri");

  return {
    version,
    canonicalUri,
    canonicalVersionUri,
  };
}

function parseColumnSet(node: Element): ColumnSet {
  const keyColumns: KeyColumn[] = [];
  const columns: string[] = [];

  for (const child of node.children) {
    switch (child.localName) {
      case "Key": {
        const keyId = child.getAttribute("Id");
        keyColumns.push(parseKeyColumn(child, keyId));
        break;
      }
      case "Column": {
        const keyId = child.getAttribute("Id");
        if (keyId === null) {
          throw new Error("Missing attribute `Id` in <Column> node");
        }

        if (columns.includes(keyId)) {
          throw new Error(`Duplicate column id '${keyId}'`);
        }

        columns.push(keyId);
        break;
      }
      default:
        continue;
    }
  }

  const defaultCodeKey = getDefaultCodeKey(keyColumns);
  const defaultNameKey = getDefaultNameKey(keyColumns);

  return {
    defaultCodeKey,
    defaultNameKey,
    columns,
  };
}

function getDefaultCodeKey(keyColumns: KeyColumn[]): string {
  /**
   * Extract the default code column of the code list based on the provided
   * key columns.
   *
   * This is only based on conventions of how the keys are defined in different contexts.
   * For example, XRepository has a different convention than FRED classic.
   * This function should extract the correct default key in both szenarios.
   */

  if (keyColumns.length === 0) throw new Error("Missing Key node");

  for (const key_column of keyColumns) {
    // First look for the default key as defined in FRED Classic.
    // The key sometimes has an empty reference, in which case the column names
    // are standarized. The column 'Code' is then always the default key column.
    if (key_column.id.toLowerCase() === "codekey") {
      return key_column.ref || "Code";
    }
  }

  // Just return the first available key column.
  // This should be okay, as xdf3 is not making any guaranties about which column is selected by
  // default if the user does not explicitly specify a `codeKey` within the xdf3 schema.
  for (const keyColumn of keyColumns) {
    if (keyColumn.ref !== undefined) {
      return keyColumn.ref;
    }
  }

  throw new Error("Cannot find key column");
}

function getDefaultNameKey(keyColumns: KeyColumn[]): string | undefined {
  if (keyColumns.length === 0) throw new Error("Missing Key node");

  for (const keyColumn of keyColumns) {
    if (keyColumn.id.toLowerCase() === "codenamekey") {
      return keyColumn.ref;
    }
  }
}

function parseKeyColumn(node: Element, keyId: string | null): KeyColumn {
  if (keyId === null) throw new Error("Missing attribute `Id` in <Key> node");

  const references: (string | undefined)[] = [];

  for (const child of node.children) {
    switch (child.localName) {
      case "ColumnRef": {
        if (references.length > 0)
          throw new Error("Multi-Column Keys are currently not supported");

        const ref = child.getAttribute("Ref");
        if (ref === null)
          throw new Error("Missing attribute `Ref` in <ColumnRef> node");

        if (ref === "") references.push(undefined);
        else references.push(ref);

        break;
      }
      default:
        continue;
    }
  }

  if (references.length === 0) throw new Error("Missing ColumnRef node");

  assert(references.length === 1);

  return { id: keyId, ref: references[0] };
}

function parseSimpleCodeList(node: Element): Record<string, string>[] {
  const rows: Record<string, string>[] = [];

  for (const child of node.children) {
    if (child.localName === "Row") {
      const row = parseRow(child);
      if (row !== undefined) {
        rows.push(row);
      }
    }
  }

  return rows;
}

function parseRow(node: Element): Record<string, string> | undefined {
  const row: Record<string, string> = {};

  for (const child of node.children) {
    if (child.localName === "Value") {
      const columnRef = child.getAttribute("ColumnRef");
      if (columnRef === null) {
        throw new Error("Missing attribute `ColumnRef` in <Value> node");
      }

      if (columnRef in row) {
        throw new Error(`Duplicate reference to column ${columnRef}`);
      }

      const value = parseValue(child);
      if (value !== undefined) {
        row[columnRef] = value;
      }
    }
  }

  return row;
}

function parseValue(node: Element): string | undefined {
  /**
   * Parse nodes of the form:
   *
   *  ```xml
   * <gc:Value ColumnRef="name">
   * <gc:SimpleValue>Anschrift des Antragstellers</gc:SimpleValue>
   * </gc:Value>
   * ```
   */

  for (const child of node.children) {
    if (child.localName === "SimpleValue") {
      return parseOptionalContent(child);
    }
  }

  return undefined;
}

function parseContent(node: Element): string {
  const content = node.textContent;
  if (content === null) {
    throw new Error(`Missing content in ${node.localName}`);
  }

  return content;
}

function parseOptionalContent(node: Element): string | undefined {
  return node.textContent || undefined;
}
